import { getDay } from 'date-fns';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { DaySelector } from 'src/components/DaySelector';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import {
  buildMonthCalendarDateOptions,
  buildMonthWeekOccurrenceOptions,
} from 'src/shared/Appointments/recurringRuleFieldConstants';
import { FormAutocomplete } from 'src/shared/Forms/Fields/FormAutocomplete';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { EditEventFormValues } from './_types';

interface RecurringSettingsSectionProps {
  isRecurring?: boolean;
}

function getRecurringTypeSelectOptions(
  recurringCount: number,
  t: (key: string) => string
) {
  const plural = recurringCount > 1;
  return [
    { value: 'day' as const, label: plural ? t('frequencyDays') : t('frequencyDay') },
    { value: 'week' as const, label: plural ? t('frequencyWeeks') : t('frequencyWeek') },
    { value: 'month' as const, label: plural ? t('frequencyMonths') : t('frequencyMonth') },
    {
      value: 'month_on_day' as const,
      label: plural ? t('frequencyMonthsOnDay') : t('frequencyMonthOnDay'),
    },
    {
      value: 'month_on_date' as const,
      label: plural ? t('frequencyMonthsOnDate') : t('frequencyMonthOnDate'),
    },
  ];
}

export const RecurringSettingsSection: FC<RecurringSettingsSectionProps> = ({
  isRecurring = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.editEventDialog.recurringSettings',
  });
  const { values, setFieldValue } = useFormikContext<EditEventFormValues>();

  const recurringTypeOptions = getRecurringTypeSelectOptions(
    values.recurringCount,
    t
  );

  const monthCalendarDateOptions = buildMonthCalendarDateOptions(t);
  const monthWeekOccurrenceOptions = buildMonthWeekOccurrenceOptions(t);

  // EXCOM-2473: The weekday of the session instance the user clicked to open
  // cannot be deselected, matching legacy v2 behavior (currentScheduledDays in
  // v2/app/controllers/dashboard/fbm/modal-new-appointment.js). Only that one
  // day is locked; other days in a multi-day recurrence remain toggleable.
  const scheduledDay = values.startTime ? getDay(values.startTime) : undefined;

  const handleRecurringTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const prevType = values.recurringType;
    const recurringType = event.target.value as EditEventFormValues['recurringType'];

    if (recurringType === 'month_on_day' || recurringType === 'month_on_date') {
      setFieldValue('recurringDayNumbers', [1]);
    } else {
      setFieldValue('recurringDayNumbers', []);
    }

    // Avoid sending stale weekly day picks when the UI no longer edits recurringDays
    // (see create/update scheduled appointment payloads in useEditEventDialog).
    if (
      recurringType === 'day' ||
      recurringType === 'month' ||
      recurringType === 'month_on_date'
    ) {
      setFieldValue('recurringDays', []);
    } else if (recurringType === 'week' || recurringType === 'month_on_day') {
      const prevHidDaySelector =
        prevType === 'day' ||
        prevType === 'month' ||
        prevType === 'month_on_date';
      if (prevHidDaySelector) {
        setFieldValue('recurringDays', [getDay(values.startTime)]);
      }
    }
  };

  return (
    <Stack spacing={2}>
      {!isRecurring && (
        <Checkbox
          useSwitch
          label={t('makeRecurring')}
          checked={values.isRecurring}
          onChange={(e) => setFieldValue('isRecurring', e.target.checked)}
        />
      )}

      {values.isRecurring && (
        <Box
          component="fieldset"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            m: 0,
          }}
        >
          <Typography variant="h6" component="legend">
            {t('title')}
          </Typography>

          {isRecurring && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('isRecurringNote')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('onlyThisSessionNote')}
              </Typography>
            </>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('repeatsEvery')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  name="recurringCount"
                  label={t('intervalCount')}
                  type="number"
                  placeholder="1"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  name="recurringType"
                  label={t('intervalUnit')}
                  options={recurringTypeOptions}
                  onChange={(e) => {
                    handleRecurringTypeChange(e);
                  }}
                />
              </Grid>
            </Grid>

            {values.recurringType === 'week' && (
              <DaySelector
                label={t('repeatOn')}
                value={values.recurringDays}
                onChange={(days) => setFieldValue('recurringDays', days)}
                lockedDay={scheduledDay}
              />
            )}

            {values.recurringType === 'month_on_date' && (
              <FormAutocomplete
                name="recurringDayNumbers"
                label={t('datesOfMonth')}
                options={monthCalendarDateOptions}
                multiple
                getOptionLabel={(option: number | { id: number; name: string }) => {
                  const id = typeof option === 'number' ? option : option.id;
                  const found = monthCalendarDateOptions.find((d) => d.id === id);
                  return found?.name ?? '';
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId =
                    typeof option === 'number' ? option : option.id;
                  const valueId =
                    typeof value === 'number' ? value : value.id;
                  return optionId === valueId;
                }}
                transform={(value: unknown[]) =>
                  value.map((v) =>
                    typeof v === 'number' ? v : (v as { id: number }).id
                  )
                }
                placeholder={t('selectDatesPlaceholder')}
                limitTags={3}
              />
            )}

            {values.recurringType === 'month_on_day' && (
              <Stack spacing={2}>
                <FormAutocomplete
                  name="recurringDayNumbers"
                  label={t('weekOrdinalInMonth')}
                  options={monthWeekOccurrenceOptions}
                  multiple
                  getOptionLabel={(option: number | { id: number; name: string }) => {
                    const id = typeof option === 'number' ? option : option.id;
                    const found = monthWeekOccurrenceOptions.find(
                      (d) => d.id === id
                    );
                    return found?.name ?? '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionId =
                      typeof option === 'number' ? option : option.id;
                    const valueId =
                      typeof value === 'number' ? value : value.id;
                    return optionId === valueId;
                  }}
                  transform={(value: unknown[]) =>
                    value.map((v) =>
                      typeof v === 'number' ? v : (v as { id: number }).id
                    )
                  }
                  placeholder={t('anyWeekOrdinalPlaceholder')}
                  limitTags={2}
                />
                <DaySelector
                  label={t('repeatOn')}
                  value={values.recurringDays}
                  onChange={(days) => setFieldValue('recurringDays', days)}
                  lockedDay={scheduledDay}
                />
              </Stack>
            )}

            <FormDatePicker name="endAt" label={t('endDate')} />

            <Box>
              <Checkbox
                label={t('allowBookAllRecurring')}
                checked={values.bookRecurring}
                onChange={(e) =>
                  setFieldValue('bookRecurring', e.target.checked)
                }
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
